"use client";
import { ServiceSummary } from "@/app/(shop)/services/[slug]/components/serviceSummary";
import { setDPMenuCookie } from "@/app/actions";
import { useMerchant } from "@/app/providers/merchantProvider";
import Divider from "@/components/divider/divider";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import { MerchantLogo } from "@/components/header/merchantLogo";
import { IconSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { ScanToOrderKitchenModal } from "@/components/scanToOrder/scanToOrderCart/scanToOrderKitchenModal";
import { OrderType } from "@/constants/enum";
import { useCart } from "@/contexts/CartContext";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { getCustomerDetails } from "@/dataLayer";
import { onDeliveryAddressUpdate } from "@/dataLayer/deliveryAddressUtils";
import { CustomerDetails } from "@/dataLayer/getCustomerDetails";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import { Address } from "@/dto/address";
import { Category } from "@/dto/category";
import {
  DayPart,
  DayPartMenuCategories,
  DayPartMenuCookie,
} from "@/dto/dayPartMenu";
import { GiftCard } from "@/dto/giftCard";
import { TableData } from "@/dto/tableData";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { RetailCart } from "@/models/cart/retailCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import {
  FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2,
  ROUTE_ALL,
  ROUTE_CATEGORIES_ALL,
  ROUTE_HOME,
  ROUTE_MENU,
  ROUTE_MENU_ALL,
  ROUTE_SERVICES,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
  ROUTE_UNCATEGORISED,
  UNCATEGORISED_CATEGORY_ID,
} from "@/utils/constants";
import { giftCardCategory } from "@/utils/giftCardUtils";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { getCartStatus } from "@/utils/restaurantCartUtils";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { toastService } from "@/utils/toastService";
import useLocalStorage from "@/utils/useLocalStorage";
import useMediaQuery from "@/utils/useMediaQuery";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { Toaster } from "react-hot-toast";
import { RetailCartDrawer } from "../cartDrawer/retailCartDrawer";
import {
  ClearCartAction,
  ClearCartModal,
} from "../clearCartModal/clearCartModal";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { CartIcon } from "../header/cartIcon";
import { BusinessHoursModal } from "../header/hoursModal";
import { MobileCategoryModal } from "../header/mobileCategoryModal";
import { RestaurantCartDrawer } from "../restaurantCartDrawer/restaurantCartDrawer";
import { ScanToOrderCart } from "../scanToOrder/scanToOrderCart/scanToOrderCart";
import { CartNumber } from "./cartNumber";
import { CategoryButton } from "./categoryButton";
import { DayPartMenuSelector } from "./dayPartMenuSelector";
import { MobileNavigationBar } from "./mobileNavigationBar";
import { NavLink } from "./navLink";
import { Perks } from "./perks";
import { SearchField } from "./searchField";
import { SearchIcon } from "./searchIcon";
import { SecondaryNavigation } from "./secondaryNavigation";
import { UtilityPanel } from "./utilityPanel";
import { useNativeActions } from "@/utils/useNativeActions";
import { ScheduledTime } from "@/dto/scheduledTime";
import { useAddressDialog } from "@/utils/useAddressDialog";
import { useAuth } from "@/app/providers/authProvider";
import { useDepositPolicy } from "@/utils/useDepositPolicy";

export interface NavigationBarProps {
  menu: any;
  customerPerks: any;
  merchantPreferences: any;
  deleteSession?: any;
  retailCategories?: any;
  serviceCategories?: any;
  pickupTimePromise?: Promise<any>;
  defaultAddress: Address | null;
  isLoading: boolean;
  setIsCategoryMenuOpen: Dispatch<SetStateAction<boolean | undefined>>;
  isCategoryMenuOpen?: boolean;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
  selectedMenu?: DayPart | null;
  // eslint-disable-next-line no-unused-vars
  setSelectedMenu?: (menu: DayPart | null) => void;
  isNotAcceptingOrder: boolean;
  isScrolledDown?: boolean;
}

export const NavigationBar = ({
  menu,
  customerPerks,
  merchantPreferences,
  deleteSession,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  defaultAddress,
  isLoading,
  setIsCategoryMenuOpen,
  isCategoryMenuOpen,
  dayPartMenus,
  dayPartMenuCategories,
  selectedMenu = null,
  setSelectedMenu,
  isNotAcceptingOrder,
  isScrolledDown,
}: NavigationBarProps) => {
  const t = useTranslations("NavigationBar");
  const { merchant } = useMerchant();
  const pathName = usePathname();
  const { push, replace, refresh } = useRouter();
  const { isLoggedIn } = useAuth();
  const [cookies] = useCookies(["preview", "scanToOrderInfo"]);
  const { scanToOrderInfo } = cookies;
  const isScanToOrderFlow = !!scanToOrderInfo;
  const currentlySavedAddress = getFromLocalStorage<Address | null>(
    LocalStorageKey.DELIVERY_ADDRESS,
    null
  );
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [searchFieldOpen, setSearchFieldOpen] = useState<boolean>(false);
  const [perksModalIsOpen, setPerksModalIsOpen] = useState<boolean>(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState<boolean>(false);
  const { cart } = useCart();
  const storedCart = cart?.items || [];
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [tempAddress, setTempAddress] = useState(currentlySavedAddress ?? "");
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);
  const [menuState, setMenuState] = useState<Category[]>(menu);
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);
  const [, setCustomerDetails] = useState<Partial<CustomerDetails> | null>(
    null
  );
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] =
    useState<boolean>(false);
  const [tableData, setTableData] = useState<TableData | null>();
  const [addedToCartBannerShowing, setAddedToCartBannerShowing] =
    useState<boolean>(false);
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const searchParams = useSearchParams();

  const { isCmaApp, openLoginSignUp } = useNativeActions();
  const depositMightApply = useDepositPolicy(merchant, merchantPreferences);

  const orderEstimateUpdateInterval = useRef(undefined);
  const [storedGiftCard] = useLocalStorage<GiftCard | null>(
    LocalStorageKey.GIFT_CARD,
    null
  );
  const shouldRenderRestaurantCart = merchant.isRestaurant;
  const shouldRenderRetailCart =
    merchant.isRetail ||
    merchant.isHybrid ||
    (merchant?.isService && merchant?.hasOnlyRetailItems) ||
    storedGiftCard;
  const showPerks =
    !merchant.isService ||
    (merchant.isService &&
      (merchantPreferences?.appointmentPref?.apptPayment === "FULL" ||
        merchantPreferences?.appointmentPref?.cancellationNoShowFeeEnabled || depositMightApply));
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";
  const prevSession = useRef(isLoggedIn);
  const isUserInitiatedDayPartSwitch = useRef(false);

  const nonEssentialRestaurant =
    merchant.isRestaurant && merchant.isCheckoutEnabled;
  const nameToUseOnMenuLink = `${
    nonEssentialRestaurant ? t("menu") : t("categories")
  }`;
  let queryParams = "";
  const menuName = searchParams?.get("menu");
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  if (menuName) {
    queryParams += queryParams ? `&menu=${menuName}` : `?menu=${menuName}`;
  }

  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const tempMenu = useRef<DayPart | null>(null);

  const retailPickupTime = pickupTimePromise ? use(pickupTimePromise) : null;
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const hasHorizontalScrollFlag = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2
  );

  useEffect(() => {
    if (
      isLoggedIn &&
      !currentlySavedAddress &&
      defaultAddress &&
      Object.keys(defaultAddress).length > 0
    ) {
      saveToLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, defaultAddress);
      setTempAddress(defaultAddress);
    }
  }, [defaultAddress]);

  useEffect(() => {
    const enforceAuthRules = async () => {
      if (isDineIn && isScanToOrderFlow && isLoggedIn) {
        await deleteSession();
        refresh();
        return;
      }

      const isNewLogin = isLoggedIn && !prevSession.current;
      if (isNewLogin && !isDineIn) {
        await handleCustomerInitialization();
      }

      prevSession.current = isLoggedIn;
    };

    const handleCustomerInitialization = async () => {
      try {
        const details = await getCustomerDetails(merchant.id);
        if (details) {
          setCustomerDetails(details);
          toastService.notifyUserLoggedIn(isMobileScreen, details?.firstName);
        }
      } catch (error) {
        console.error("User logged in, but profile fetch failed:", error);

        setCustomerDetails(null);
      }
    };

    enforceAuthRules();
  }, [isLoggedIn, isDineIn, refresh, merchant.id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAddedToCartBannerShowing(false);
    }, 3000);
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LocalStorageKey.CURRENT_CART_EVENT) {
        toastService.notifyItemAdded();
      }
    };
    globalThis.addEventListener("storage", handleStorageChange);
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      clearTimeout(timeout);
    };
  }, [addedToCartBannerShowing]);

  useEffect(() => {
    setSearchFieldOpen(false);
    setSearchQuery("");
    if (globalThis.location.href?.includes("#cart")) {
      setIsRetailCartOpen(true);
    }
    if (globalThis.location.href?.includes("#restaurant-cart")) {
      setIsRestaurantCartOpen(true);
    }
    if (
      isScanToOrderFlow &&
      !globalThis.location.href?.includes("#restaurant-cart")
    ) {
      setIsRestaurantCartOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  useEffect(() => {
    setIsPreview(globalThis.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    const getTableData = setInterval(() => {
      const tableData = getFromLocalStorage<TableData | undefined>(
        LocalStorageKey.TABLE_DATA,
        undefined
      );
      if (tableData && scanToOrderInfo) {
        setTableData(tableData);
        if (tableData?.order?.id) {
          saveToSessionStorage(
            SessionStorageKey.RECEIPT_ID,
            tableData?.order?.id
          );
        }
      } else {
        setTableData(null);
      }
    }, 1000);
    return () => clearInterval(getTableData);
  }, []);

  let uncategorisedItems = useMemo(() => {
    return menuState?.find(
      (category: any) => category.id === UNCATEGORISED_CATEGORY_ID
    );
  }, [menuState]);

  let categories = useMemo(() => {
    if (!menuState) return [];

    return (
      Object.values(
        menuState.filter((item: any) => item.id !== UNCATEGORISED_CATEGORY_ID)
      ) as any[]
    ).sort((a: any, b: any) => {
      const orderA = (a as { sortOrder: number }).sortOrder;
      const orderB = (b as { sortOrder: number }).sortOrder;
      return orderA - orderB;
    });
  }, [menuState]);

  const uncategorized = {
    id: UNCATEGORISED_CATEGORY_ID,
    items: [],
    name: t("uncategorized"),
    sortOrder: Number.MAX_SAFE_INTEGER,
  };

  if (
    uncategorisedItems &&
    uncategorisedItems?.numberOfItems != 0 &&
    !categories.some((category) => category.id === UNCATEGORISED_CATEGORY_ID)
  ) {
    if (merchant.isSellingGiftCards) {
      // Insert before last category which is gift card category
      categories.splice(categories.length - 1, 0, uncategorized);
    } else {
      categories.push(uncategorized);
    }
  }

  let navigationCategories = merchantMeta?.navigationCategories;
  const mappedCategories: Category[] = useMemo(() => {
    // Safety check to proactively choose categories
    const isArray = Array.isArray(navigationCategories);
    const isEmpty = isArray && navigationCategories.length === 0;

    if (!isArray || isEmpty) {
      const MAX_CATEGORIES_DISPLAY = 5;
      return categories
        .filter((category: Category) => category.id !== giftCardCategory.id)
        .slice(0, MAX_CATEGORIES_DISPLAY);
    }

    return navigationCategories
      ?.map((orderedCategory: any) => {
        const categoryDetails = categories.find(
          (category) => category.id === orderedCategory.value
        );
        return categoryDetails ?? null;
      })
      ?.filter((category: any) => category !== null);
  }, [categories, navigationCategories]);

  const remainingCategories = categories.filter((category) => {
    return !mappedCategories?.some(
      (mappedCategory: any) => mappedCategory.id === category.id
    );
  });

  const moreCategories = remainingCategories;

  useEffect(() => {
    const MAX_CATEGORIES_DISPLAY = 5;
    const REMAINING_DROPDOWN_CATEGORY = 1;
    // Promote the sixth category to be shown in mapped categories
    if (
      navigationCategories?.length === MAX_CATEGORIES_DISPLAY &&
      remainingCategories.length === REMAINING_DROPDOWN_CATEGORY
    ) {
      mappedCategories.push(remainingCategories.shift()!);
    }
  }, [navigationCategories, moreCategories, remainingCategories]);

  const handlePerksModal = () => {
    setPerksModalIsOpen(!perksModalIsOpen);
  };

  const handleLoginModal = (toCheckout: boolean) => {
    setIsRetailCartOpen(false);
    setToCheckoutAfterLogin(toCheckout);
    if (isCmaApp) {
      if (!loginModalIsOpen) {
        openLoginSignUp(toCheckout ? "checkoutButton" : "profileIcon");
      }
    } else {
      setLoginModalIsOpen(!loginModalIsOpen);
    }
  };

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };
  const serviceCart = ServiceCart.getItems();
  const retailCart = RetailCart.getItems();
  const restaurantCart = RestaurantCart.getItems();

  const {
    isRetailCartOpen,
    setIsRetailCartOpen,
    isRestaurantCartOpen,
    setIsRestaurantCartOpen,
    isServiceSummaryOpen: isAppointmentSummaryOpen,
    setIsServiceSummaryOpen: setIsAppointmentSummaryOpen,
    toggleCartDrawer: handleCartDrawer,
    isHandlingCartDrawer,
    setIsHandlingCartDrawer,
    isProceedingToCheckout,
    openCartDrawer,
  } = useCartDrawer();

  const [isScanToOrderKitchenModalOpen, setIsScanToOrderKitchenModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const openAddressFlow = useAddressDialog(setDeliveryAddressModalOpen);

  // Native app address listener
  useEffect(() => {
    const handleNativeAddressUpdated = async () => {
      const newAddress = getFromLocalStorage<Partial<Address>>(
        LocalStorageKey.DELIVERY_ADDRESS,
        {}
      );

      if (newAddress?.address1) {
        const validAddress = newAddress as Address;

        setTempAddress(validAddress);

        await onDeliveryAddressUpdate(validAddress, false, merchant.id, {
          openCartDrawer,
          setDeliveryAddressModalOpen,
          setDeliveryAddressErrorModalOpen,
        });
      }
    };

    globalThis.addEventListener(
      "DeliveryAddressUpdated",
      handleNativeAddressUpdated
    );

    return () => {
      globalThis.removeEventListener(
        "DeliveryAddressUpdated",
        handleNativeAddressUpdated
      );
    };
  }, [merchant?.id, onDeliveryAddressUpdate, openCartDrawer]);

  useEffect(() => {
    // restaurant: menu item modal
    const shouldRedirectRestaurant =
      isHandlingCartDrawer && !isRestaurantCartOpen && merchant.isRestaurant;

    if (
      shouldRedirectRestaurant &&
      pathName != null &&
      pathName.startsWith(ROUTE_MENU) &&
      pathName !== ROUTE_MENU_ALL
    ) {
      replace(`${ROUTE_MENU_ALL}${queryParams}`);
    }
    if (shouldRedirectRestaurant && pathName === ROUTE_MENU_ALL) {
      setIsHandlingCartDrawer(false);
    }
  }, [isRestaurantCartOpen, isHandlingCartDrawer]);

  useEffect(() => {
    // services or (hybrid w/ serviceCart): service item modal
    const isReadyToHandle =
      isHandlingCartDrawer &&
      !isAppointmentSummaryOpen &&
      !isProceedingToCheckout;

    const shouldRedirectService =
      merchant.isService &&
      !merchant.isHybrid &&
      !merchant.hasOnlyRetailItems &&
      !storedGiftCard;

    const shouldRedirectHybridService =
      merchant.isHybrid &&
      (serviceCart?.length > 0 || (!retailCart?.length && !storedGiftCard));

    if (
      isReadyToHandle &&
      pathName != null &&
      pathName.startsWith(ROUTE_SERVICES) &&
      pathName !== ROUTE_SERVICES_ALL &&
      (shouldRedirectService || shouldRedirectHybridService)
    ) {
      replace(ROUTE_SERVICES_ALL);
    }
    if (isReadyToHandle && pathName === ROUTE_SERVICES_ALL) {
      setIsHandlingCartDrawer(false);
    }
  }, [isAppointmentSummaryOpen, isHandlingCartDrawer, isProceedingToCheckout]);

  useEffect(() => {
    // retail or (hybrid w/ no serviceCart nor retailCart): no item modal (could be giftcard or retail item page)
    const isReadyToHandle = isHandlingCartDrawer && !isRetailCartOpen;
    const isRetail = merchant.isRetail;
    const isHybridWithEmptyCarts =
      merchant.isHybrid && !retailCart?.length && !serviceCart?.length;

    if (isReadyToHandle && (isRetail || isHybridWithEmptyCarts)) {
      setIsHandlingCartDrawer(false);
    }

    // (hybrid or services) w/ retailCart: retail item modal
    const hasRetailItemsInCart = retailCart.length > 0 && !storedGiftCard;
    const shouldRedirectHybridRetail =
      merchant.isHybrid && hasRetailItemsInCart;
    const shouldRedirectServiceRetail =
      merchant.isService && merchant.hasOnlyRetailItems && hasRetailItemsInCart;

    if (
      isReadyToHandle &&
      pathName != null &&
      pathName.startsWith(ROUTE_SHOP) &&
      pathName !== ROUTE_SHOP &&
      (shouldRedirectHybridRetail || shouldRedirectServiceRetail)
    ) {
      replace(ROUTE_SHOP);
    }
    if (isReadyToHandle && pathName === ROUTE_SHOP) {
      setIsHandlingCartDrawer(false);
    }
  }, [isRetailCartOpen, isHandlingCartDrawer]);

  const onDayPartMenuChange = useCallback(
    (menu: DayPart | null) => {
      const shouldShowClearCartForMenuSwitch =
        restaurantCart.length > 0 && !isDineIn && selectedMenu?.id !== menu?.id;

      isUserInitiatedDayPartSwitch.current = true;
      tempMenu.current = menu;
      if (shouldShowClearCartForMenuSwitch) {
        setIsClearCartOpen(true);
        return;
      }

      switchDayPartMenu();
    },
    [restaurantCart, isDineIn, selectedMenu]
  );

  const onConfirmation = () => {
    RestaurantCart.clear();
    setIsClearCartOpen(false);
    switchDayPartMenu();
  };

  const switchDayPartMenu = () => {
    if (tempMenu.current) {
      setSelectedMenu?.(tempMenu.current);
      removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
      removeFromSessionStorage(SessionStorageKey.PICKUP_TIME);
      const payload: DayPartMenuCookie = {
        id: tempMenu.current.id,
        menuId: tempMenu.current.menuId,
        name: tempMenu.current.formattedName,
      };
      saveToSessionStorage(SessionStorageKey.DAYPART_MENU, payload);
      setDPMenuCookie(payload);
      const params = new URLSearchParams(searchParams || "");
      params.set("menu", tempMenu.current?.formattedName);
      push(`${ROUTE_MENU_ALL}?${params.toString()}`);
    }
  };

  const clearCartActions: ClearCartAction[] = [
    {
      text: t("cancel"),
      onClick: () => setIsClearCartOpen(false),
      type: "secondary",
    },
    {
      text: t("clearCartAndSwitchMenu"),
      onClick: onConfirmation,
      type: "primary",
    },
  ];

  useEffect(() => {
    if (!selectedMenu) return;
    const categories =
      dayPartMenuCategories?.find((o) => o.menuId === selectedMenu?.menuId)
        ?.categories || [];
    setMenuState(categories);

    if (!isUserInitiatedDayPartSwitch.current) return;

    // observes after day part menu page loads for the first category ins secondary navigation to focus
    const observer = new MutationObserver((mutations, obs) => {
      const firstSecondaryNavOption = document.getElementById(
        pom.accessibilityIds.secondaryNavStart
      );
      if (firstSecondaryNavOption) {
        firstSecondaryNavOption.focus();

        obs.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // after 5 seconds, stop checking body for first day part menu category in secondary navigation
    const timeoutId = setTimeout(() => {
      isUserInitiatedDayPartSwitch.current = false;
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [selectedMenu]);

  // update order estimate every 60 seconds if once of the carts is open
  useEffect(() => {
    const updateOrderEstimates = async () => {
      const orderEstimates = await getOrderEstimate(
        merchant.id,
        deliveryAddress,
        merchant?.isRetail ? retailPickupTime?.pickupDateTimeMillis : undefined
      );
      saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, orderEstimates);
    };
    const deliveryAddress = getFromLocalStorage<Partial<Address>>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {}
    );
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );

    const savedOrderType = sessionTime.orderType ?? null;
    if (
      (isRetailCartOpen || isRestaurantCartOpen) &&
      !isEmpty(deliveryAddress) &&
      savedOrderType === OrderType.DELIVERY &&
      !orderEstimateUpdateInterval.current
    ) {
      updateOrderEstimates();
      (orderEstimateUpdateInterval.current as any) = setInterval(async () => {
        updateOrderEstimates();
      }, 60 * 1000);
    } else {
      clearInterval(orderEstimateUpdateInterval.current);
      orderEstimateUpdateInterval.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetailCartOpen, isRestaurantCartOpen]);

  const handleSearch = (event: { key: string }) => {
    if (event.key === "Enter") {
      const params = new URLSearchParams(searchParams || "");
      if (searchQuery) {
        params.set("query", searchQuery);
      } else {
        params.delete("query");
      }
      if (pathName?.toLocaleLowerCase().includes("shop")) {
        push(`${ROUTE_SHOP}?${params.toString()}`);
      } else if (merchant.isService) {
        push(`${ROUTE_SERVICES_ALL}?${params.toString()}`);
      } else if (merchant.isRestaurant) {
        push(`${ROUTE_MENU_ALL}?${params.toString()}`);
      } else if (merchant.isEssentialMerchant) {
        push(`${ROUTE_CATEGORIES_ALL}?${params.toString()}`);
      } else {
        push(`${ROUTE_ALL}?${params.toString()}`);
      }
      setSearchFieldOpen(false);
    }
  };

  const showMoreDropDown = useMemo(() => {
    const currentCategoryInPathName = decodeURIComponent(pathName!).includes(
      moreCategories[0]?.name
    );
    if (
      (moreCategories.length === 1 && currentCategoryInPathName) ||
      moreCategories.length < 1
    ) {
      return false;
    } else {
      return true;
    }
  }, [moreCategories]);

  const { cartValue, shouldShow } = useMemo(
    () => getCartStatus(storedCart, storedGiftCard, tableData),
    [storedCart, storedGiftCard, tableData]
  );

  const getAriaLabelForCart = () => {
    if (!shouldShow) return t("openCart");

    if (merchant.isHybrid) {
      const retailCart = RetailCart.getItems();
      const serviceCart = ServiceCart.getItems();
      if (retailCart.length > 0 || storedGiftCard)
        return t("openRetailCart", { count: cartValue });
      if (serviceCart.length > 0)
        return t("openServicesCart", { count: cartValue });
    }

    if (merchant.isService) {
      if (merchant.hasOnlyRetailItems || storedGiftCard)
        return t("openRetailCart", { count: cartValue });

      return t("openServicesCart", { count: cartValue });
    }

    return t("openRetailCart", { count: cartValue });
  };

  const menuCategoriesText = `${t("menu")} ${t("categories")}`;

  return (
    <>
      {isClearCartOpen && (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={() => setIsClearCartOpen(false)}
          merchantColor={merchantColor}
          addToCart={onConfirmation}
          actions={clearCartActions}
          title={t("clearCartTitle")}
          message={t("clearCartMessage", {
            menuName: tempMenu.current?.menuName ?? "",
          })}
        />
      )}
      <div className="flex w-full items-center justify-between sticky sm:relative top-0 z-40">
        <Toaster />
        <div className="flex sm:hidden py-5 px-3 sm:px-8 ">
          <button
            type="button"
            onClick={() => setSideMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            data-testid={applyDataTestId(pom.header.hamburgerButton)}
          >
            <span className="sr-only">{t("openMainMenu")}</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        {searchFieldOpen && (
          <SearchField
            {...{
              searchParams,
              setSearchQuery,
              handleSearch,
              setSearchFieldOpen,
            }}
          />
        )}
        {!searchFieldOpen && (
          <>
            {!merchant.isRetail &&
              (pathName?.includes("services") ||
                pathName?.includes("shop") ||
                pathName?.includes("categories") ||
                pathName?.includes("menu")) && (
                <CategoryButton
                  setIsCategoryMenuOpen={setIsCategoryMenuOpen}
                  merchant={merchant}
                  selectedDayPart={selectedMenu}
                />
              )}
            <div
              className={`flex flex-col sm:w-full items-end sm:items-center justify-between ${
                merchant.isRetail ||
                (!pathName?.includes("shop") &&
                  !pathName?.includes("services") &&
                  !pathName?.includes("categories") &&
                  !pathName?.includes("menu"))
                  ? "w-full"
                  : ""
              }`}
            >
              <nav
                aria-label={t("navigationBarLabel")}
                data-testid={applyDataTestId(pom.header.navigationBar)}
                className={[
                  "flex",
                  "items-center",
                  "justify-between",
                  "sm:w-full",
                  "sm:px-10",
                  `${!isMobileScreen && isScrolledDown ? "pb-3" : ""}`,
                ].join(" ")}
              >
                <PopoverGroup
                  className={clsx(
                    "hidden",
                    "h-full",
                    "sm:flex",
                    !isMobileScreen && !isScrolledDown ? "pt-5" : "pt-0 gap-2.5"
                  )}
                  data-testid={applyDataTestId(
                    pom.header.navigationLinksContainer
                  )}
                >
                  <div
                    className={clsx("transition-all duration-[1.4s] ease-out", {
                      "absolute left-[-9999px] max-w-0 opacity-0 invisible":
                        !isMobileScreen && !isScrolledDown,
                      "max-w-full": !isMobileScreen && isScrolledDown,
                    })}
                    aria-hidden={!isMobileScreen && !isScrolledDown}
                  >
                    <MerchantLogo
                      showLink={
                        !isDineIn && !merchant.isStandaloneGiftCardWebsite
                      }
                      linkHref={ROUTE_HOME}
                      merchantLogo={merchant.logo}
                    />
                  </div>

                  {merchant.isService && !merchant.hasOnlyRetailItems && (
                    <NavLink
                      name={t("services")}
                      nameToUse="services/all"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes(ROUTE_SERVICES)}
                      data-testid={applyDataTestId(
                        pom.navigationBar.servicesLink
                      )}
                      skipToID={pom.accessibilityIds.secondaryNavStart}
                    />
                  )}
                  {(merchant.isHybrid ||
                    (merchant.isService && merchant.hasOnlyRetailItems)) && (
                    <NavLink
                      name={t("shop")}
                      nameToUse="shop"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes(ROUTE_SHOP)}
                      data-testid={applyDataTestId(pom.navigationBar.shopLink)}
                      skipToID={pom.accessibilityIds.secondaryNavStart}
                    />
                  )}
                  {(merchant.isRestaurant || merchant.isEssentialMerchant) && (
                    <>
                      {dayPartMenus && dayPartMenus.length > 0 ? (
                        <DayPartMenuSelector
                          dayPartMenus={dayPartMenus}
                          selectedMenu={selectedMenu}
                          onMenuChange={onDayPartMenuChange}
                          merchant={merchant}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            menuCategoriesText
                              .toLowerCase()
                              .includes(nameToUseOnMenuLink?.toLowerCase()) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                        />
                      ) : (
                        <NavLink
                          name={`${nameToUseOnMenuLink}`}
                          nameToUse={`${nameToUseOnMenuLink.toLowerCase()}${ROUTE_ALL}${queryParams}`}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            menuCategoriesText
                              .toLowerCase()
                              .includes(nameToUseOnMenuLink?.toLowerCase()) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                          skipToID={pom.accessibilityIds.secondaryNavStart}
                        />
                      )}
                    </>
                  )}

                  {merchant.isRetail &&
                    mappedCategories?.map((c: { id: string; name: string }) => {
                      const categoryHeaderID = `${c.name.replaceAll(
                        /\s+/g,
                        "-"
                      )}-header`;
                      return (
                        <NavLink
                          key={c.id}
                          nameToUse={
                            c.id === uncategorized.id
                              ? ROUTE_UNCATEGORISED
                              : encodeURIComponent(c.name)
                          }
                          name={encodeURIComponent(c.name)}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={false}
                          data-testid={applyDataTestId(
                            pom.navigationBar.selectedCategories
                          )}
                          skipToID={categoryHeaderID}
                        />
                      );
                    })}
                  {/* This component appears on URL match */}
                  {moreCategories.some(
                    (c: { id: string; name: string }) =>
                      decodeURIComponent(pathName || "").replace("/", "") ===
                      c.name
                  ) && (
                    <NavLink
                      name={(pathName || "").substring(1)}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={
                        ((pathName || "").substring(1) === "services" &&
                          pathName?.includes(ROUTE_SERVICES)) ||
                        (menuCategoriesText.includes(
                          (pathName || "").substring(1)
                        ) &&
                          pathName?.includes(nameToUseOnMenuLink))
                      }
                      skipToID={`${pathName.replaceAll(/\s+/g, "-")}-header`}
                    />
                  )}
                  {merchant.isRetail && showMoreDropDown && (
                    <Popover
                      className="relative"
                      data-testid={applyDataTestId(
                        pom.navigationBar.moreDropdown
                      )}
                    >
                      <PopoverButton className="w-full h-full capitalize leading-6 text-gray-900 px-4 hover:bg-gray-50 text-center pb-3 text-[16px] flex gap-x-2 items-center link">
                        {t("more")}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="h-5 w-5 flex-none text-gray-900"
                        />
                      </PopoverButton>
                      <PopoverPanel
                        transition
                        className="navbar-dropdown-scrollbar absolute -left-8 top-full z-20 mt-3 w-screen max-w-[400px] overflow-x-hidden overflow-y-auto rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] px-6 py-5"
                      >
                        {moreCategories
                          .filter(
                            (c: { id: string; name: string }) =>
                              decodeURIComponent(pathName || "").replace(
                                "/",
                                ""
                              ) !== c.name
                          )
                          .map((c: { id: string; name: string }) => (
                            <CloseButton
                              as={Link}
                              key={c.id}
                              href={
                                c.id === uncategorized.id
                                  ? `/${ROUTE_UNCATEGORISED}`
                                  : `/${encodeURIComponent(c.name)}` // for localization
                              }
                              className="group relative flex items-center gap-x-6 rounded-lg py-3 text-normal leading-6 hover:bg-gray-50 text-left w-full"
                              aria-label={`${c.name},`}
                            >
                              {c.name}
                            </CloseButton>
                          ))}
                      </PopoverPanel>
                    </Popover>
                  )}
                  {merchant.isSellingGiftCards && !isDineIn && (
                    <NavLink
                      name={t("giftCard")}
                      nameToUse="giftcard"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/giftcard")}
                      data-testid={applyDataTestId(pom.giftCard.navLink)}
                    />
                  )}

                  {merchantMeta?.selectedSetup !== "Simple" && (
                    <NavLink
                      name={t("about")}
                      nameToUse="about"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/about")}
                      queryParams={queryParams}
                      data-testid={applyDataTestId(
                        pom.navigationBar.aboutSection
                      )}
                    />
                  )}
                </PopoverGroup>
                <div
                  className={[
                    "flex",
                    "flex-row",
                    "gap-5",
                    "items-center",
                    "mr-4",
                  ].join(" ")}
                >
                  {isLoggedIn &&
                    customerPerks?.length > 0 &&
                    showPerks &&
                    merchant.isCheckoutEnabled &&
                    !tableData &&
                    !isCmaApp && (
                      <Perks
                        {...{
                          handlePerksModal,
                          perksModalIsOpen,
                          customerPerks,
                        }}
                      />
                    )}
                  {!isPreview && !pathName?.includes("/giftcard") && (
                    <button
                      className="cursor-pointer border-none"
                      onClick={() => setSearchFieldOpen(true)}
                      type="button"
                      aria-label={t("globalSearchDescriptiveText")}
                    >
                      <SearchIcon />
                    </button>
                  )}
                  <BusinessHoursModal
                    isOpen={hoursModalIsOpen}
                    onRequestClose={handleHoursModal}
                    businessHours={merchant?.businessHours?.hours}
                    timezone={merchant.timezone}
                  />
                  {!isPreview &&
                    merchant.isCheckoutEnabled &&
                    customerAuthEnabled &&
                    !isScanToOrderFlow &&
                    (isLoading ? (
                      <IconSkeleton width={20} height={20} />
                    ) : (
                      <UtilityPanel
                        {...{
                          handleLoginModal,
                          loginModalIsOpen,
                          setLoginModalIsOpen,
                          deleteSession,
                          merchantColor,
                          toCheckoutAfterLogin,
                        }}
                        merchant={merchant}
                      />
                    ))}
                  {merchant.isCheckoutEnabled && !isPreview && (
                    <button
                      className={[
                        "cursor-pointer",
                        "relative",
                        "inline-flex",
                        "sm:mr-0",
                      ].join(" ")}
                      onClick={handleCartDrawer}
                      data-testid={applyDataTestId(pom.landingPage.cartIcon)}
                      aria-label={getAriaLabelForCart()}
                      type="button"
                    >
                      <CartIcon />
                      <CartNumber
                        merchantColor={merchantColor}
                        cartValue={cartValue}
                        shouldShow={shouldShow}
                      />
                    </button>
                  )}
                  {shouldRenderRetailCart && (
                    <RetailCartDrawer
                      isOpen={isRetailCartOpen}
                      onClose={() => setIsRetailCartOpen(false)}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      onTriggerLoginModal={handleLoginModal}
                      retailPickupTime={retailPickupTime}
                      setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                      setIsRetailCartOpen={
                        setIsRetailCartOpen as (
                          value?: boolean | undefined
                        ) => void
                      }
                      setDeliveryAddressErrorModalOpen={
                        setDeliveryAddressErrorModalOpen
                      }
                    />
                  )}
                  <ServiceSummary
                    isOpen={isAppointmentSummaryOpen}
                    onClose={() => setIsAppointmentSummaryOpen(false)}
                    onRequestClose={handleCartDrawer}
                    merchant={merchant}
                    onTriggerLoginModal={handleLoginModal}
                    merchantPreferences={merchantPreferences}
                    merchantColor={merchantColor}
                    setIsRetailCartOpen={setIsAppointmentSummaryOpen}
                  />
                  {!tableData &&
                    shouldRenderRestaurantCart &&
                    !isScanToOrderFlow && (
                      <RestaurantCartDrawer
                        isOpen={isRestaurantCartOpen}
                        onClose={() => setIsRestaurantCartOpen(false)}
                        onRequestClose={handleCartDrawer}
                        merchant={merchant}
                        onTriggerLoginModal={handleLoginModal}
                        setDeliveryAddressModalOpen={
                          setDeliveryAddressModalOpen
                        }
                        setIsRestaurantCartOpen={setIsRestaurantCartOpen}
                        setDeliveryAddressErrorModalOpen={
                          setDeliveryAddressErrorModalOpen
                        }
                        selectedDayPart={selectedMenu}
                      />
                    )}
                  {tableData && (
                    <ScanToOrderCart
                      isOpen={isRestaurantCartOpen}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      tableData={tableData}
                      setIsSentToKitchen={setIsScanToOrderKitchenModalOpen}
                      scanToOrderInfo={scanToOrderInfo}
                      isRunningTabsEnabled={merchant.isRunningTabsEnabled}
                      selectedDayPart={selectedMenu}
                    />
                  )}
                  <ScanToOrderKitchenModal
                    isOpen={isScanToOrderKitchenModalOpen}
                    onRequestClose={() =>
                      setIsScanToOrderKitchenModalOpen(false)
                    }
                    merchant={merchant}
                    scanToOrderInfo={scanToOrderInfo}
                  />
                  <EditAddressesModal
                    setDefaultDeliveryAllowed={isLoggedIn}
                    isOpen={deliveryAddressModalOpen}
                    onRequestClose={() => setDeliveryAddressModalOpen(false)}
                    onUpdated={async (
                      address: any,
                      isDefaultDelivery: boolean
                    ) => {
                      setTempAddress(address);
                      await onDeliveryAddressUpdate(
                        address,
                        isDefaultDelivery,
                        merchant.id,
                        {
                          openCartDrawer,
                          setDeliveryAddressModalOpen,
                          setDeliveryAddressErrorModalOpen,
                        }
                      );
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                  <DeliveryModalError
                    onRequestClose={() =>
                      setDeliveryAddressErrorModalOpen(false)
                    }
                    isOpen={deliveryAddressErrorModalOpen}
                    address={tempAddress}
                    onEnterNewAddress={() => {
                      setDeliveryAddressErrorModalOpen(false);
                      openAddressFlow();
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                </div>
              </nav>
              <Divider classNames="w-[calc(100%-80px)]"></Divider>
              {!isMobileScreen && (
                <SecondaryNavigation
                  pathName={pathName}
                  merchant={merchant}
                  hasHorizontalScrollFlag={hasHorizontalScrollFlag}
                  retailCategories={retailCategories}
                  serviceCategories={serviceCategories}
                  isScrolledDown={isScrolledDown}
                  mappedCategories={mappedCategories}
                  moreCategories={moreCategories}
                  scanToOrderInfo={scanToOrderInfo}
                  isNotAcceptingOrder={isNotAcceptingOrder}
                  categories={categories}
                />
              )}
            </div>
          </>
        )}
        <MobileNavigationBar
          categories={categories}
          sideMenuOpen={sideMenuOpen}
          setSideMenuOpen={setSideMenuOpen}
          scanToOrderInfo={scanToOrderInfo}
          merchant={merchant}
          dayPartMenus={dayPartMenus}
          onDayPartMenuChange={onDayPartMenuChange}
          selectedMenu={selectedMenu}
        />
        {!merchant.isRetail &&
          (pathName?.includes("all") || pathName?.includes("shop")) && (
            <MobileCategoryModal
              menu={categories}
              setIsCategoryMenuOpen={setIsCategoryMenuOpen}
              isCategoryMenuOpen={isCategoryMenuOpen}
              merchant={merchant}
              serviceCategories={serviceCategories}
              retailCategories={retailCategories}
            />
          )}
      </div>
    </>
  );
};
